import type { Persona } from '../types/survey';

// ─────────────────────────────────────────────────
// Persona Theme Configuration
// Single source of truth for all persona-specific UI tokens
// ─────────────────────────────────────────────────

export interface PersonaThemeColors {
  primary: string;
  primaryLight: string;
  primaryDark: string;
  accent: string;
  accentLight: string;
  surface: string;
  surfaceBorder: string;
}

export interface PersonaThemeTypography {
  /** Root font size (applies to data-persona CSS) */
  baseFontSize: string;
  /** Heading scale multiplier relative to base */
  headingScale: number;
}

export interface PersonaThemeLayout {
  /** Tailwind max-width class */
  containerMaxWidth: string;
  /** Tailwind padding class for cards */
  cardPadding: string;
  /** Layout density */
  spacing: 'compact' | 'comfortable' | 'spacious';
}

export interface PersonaThemeTone {
  greeting: string;
  encouragement: string;
  /** CTA button verb — "해봐요!" vs "실행하기" */
  ctaVerb: string;
}

export interface PersonaThemeIcons {
  mascot: string;
  reward: string;
  /** Default timer duration in minutes */
  timerDefault: number;
  /** Maximum active goals allowed */
  maxActiveGoals: number;
}

export interface PersonaThemeConfig {
  colors: PersonaThemeColors;
  typography: PersonaThemeTypography;
  layout: PersonaThemeLayout;
  tone: PersonaThemeTone;
  icons: PersonaThemeIcons;
}

export const PERSONA_THEMES: Record<Persona, PersonaThemeConfig> = {
  elementary: {
    colors: {
      primary: '#f59e0b',
      primaryLight: '#fef3c7',
      primaryDark: '#d97706',
      accent: '#10b981',
      accentLight: '#d1fae5',
      surface: '#fffbeb',
      surfaceBorder: '#fde68a',
    },
    typography: {
      baseFontSize: '18px',
      headingScale: 1.75,
    },
    layout: {
      containerMaxWidth: 'max-w-md',
      cardPadding: 'p-5',
      spacing: 'spacious',
    },
    tone: {
      greeting: '안녕! 오늘도 함께 해봐요!',
      encouragement: '정말 잘했어요! 최고!',
      ctaVerb: '해봐요!',
    },
    icons: {
      mascot: '🐣',
      reward: '⭐',
      timerDefault: 15,
      maxActiveGoals: 2,
    },
  },

  secondary: {
    colors: {
      primary: '#3b82f6',
      primaryLight: '#dbeafe',
      primaryDark: '#2563eb',
      accent: '#8b5cf6',
      accentLight: '#ede9fe',
      surface: '#eff6ff',
      surfaceBorder: '#bfdbfe',
    },
    typography: {
      baseFontSize: '16px',
      headingScale: 1.5,
    },
    layout: {
      containerMaxWidth: 'max-w-lg',
      cardPadding: 'p-5',
      spacing: 'comfortable',
    },
    tone: {
      greeting: '왔구나! 오늘은 뭐 해볼까?',
      encouragement: '대박! 계속 이렇게 해보자!',
      ctaVerb: '도전하자!',
    },
    icons: {
      mascot: '🚀',
      reward: '🏆',
      timerDefault: 25,
      maxActiveGoals: 5,
    },
  },

  university: {
    colors: {
      primary: '#6366f1',
      primaryLight: '#e0e7ff',
      primaryDark: '#4f46e5',
      accent: '#06b6d4',
      accentLight: '#cffafe',
      surface: '#eef2ff',
      surfaceBorder: '#c7d2fe',
    },
    typography: {
      baseFontSize: '16px',
      headingScale: 1.4,
    },
    layout: {
      containerMaxWidth: 'max-w-lg',
      cardPadding: 'p-5',
      spacing: 'comfortable',
    },
    tone: {
      greeting: '안녕하세요! 오늘의 목표를 확인해볼까요?',
      encouragement: '좋은 성과입니다. 이 흐름을 이어가세요!',
      ctaVerb: '시작하기',
    },
    icons: {
      mascot: '🎓',
      reward: '🏅',
      timerDefault: 50,
      maxActiveGoals: 10,
    },
  },

  professional: {
    colors: {
      primary: '#475569',
      primaryLight: '#f1f5f9',
      primaryDark: '#334155',
      accent: '#0d9488',
      accentLight: '#ccfbf1',
      surface: '#f8fafc',
      surfaceBorder: '#e2e8f0',
    },
    typography: {
      baseFontSize: '16px',
      headingScale: 1.35,
    },
    layout: {
      containerMaxWidth: 'max-w-2xl',
      cardPadding: 'p-6',
      spacing: 'compact',
    },
    tone: {
      greeting: '안녕하세요. 오늘의 핵심 과제를 점검해보시겠습니까?',
      encouragement: '꾸준한 노력이 결실을 맺고 있습니다.',
      ctaVerb: '실행하기',
    },
    icons: {
      mascot: '💼',
      reward: '📊',
      timerDefault: 50,
      maxActiveGoals: 20,
    },
  },
};

/** Timer strategy options per persona */
export const PERSONA_TIMER_STRATEGIES: Record<Persona, string[]> = {
  elementary: ['반복', '읽기', '그리기', '기타'],
  secondary: ['반복학습', '문제풀기', '요약정리', '기타'],
  university: ['액티브 리콜', '포모도로', '문제 풀이', '노트 정리', '기타'],
  professional: ['집중 시간', '문서 작성', '분석/리서치', '정리/준비', '기타'],
};

/** Timer page title per persona */
export const PERSONA_TIMER_LABELS: Record<Persona, string> = {
  elementary: '집중 타이머',
  secondary: '학습 타이머',
  university: '딥 포커스',
  professional: '딥 워크 타이머',
};

/** Goal format labels per persona */
export const PERSONA_GOAL_LABELS: Record<Persona, { title: string; addButton: string }> = {
  elementary: { title: '나의 도전 카드', addButton: '새 도전!' },
  secondary: { title: '주간 미션', addButton: '미션 추가' },
  university: { title: '목표 관리', addButton: '목표 추가' },
  professional: { title: '성장 목표', addButton: '목표 추가' },
};

/** Achievement format labels per persona */
export const PERSONA_ACHIEVEMENT_LABELS: Record<Persona, { title: string; levelLabel: string }> = {
  elementary: { title: '나의 스티커 컬렉션', levelLabel: '캐릭터' },
  secondary: { title: '랭크 & 업적', levelLabel: '랭크' },
  university: { title: '역량 배지', levelLabel: '레벨' },
  professional: { title: '성과 포트폴리오', levelLabel: '등급' },
};

/** Growth profile labels per persona */
export const PERSONA_GROWTH_LABELS: Record<Persona, string> = {
  elementary: '나의 성장 캐릭터',
  secondary: '능력치 스탯',
  university: '성장 프로필',
  professional: '리더십 프로필',
};
